import React from 'react';
import { Box } from '@mui/material';
import RGL, { WidthProvider } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import { useTranslation } from 'react-i18next';

const ReactGridLayout = WidthProvider(RGL);

export interface DashboardItem {
  id: string;
  type: string;
  /** i18n key suffix for dashboard:widgetPicker.catalog.<key>.title */
  i18nKey?: string;
  /** @deprecated use i18nKey instead */
  title?: string;
  data?: any;
}

interface DashboardGridProps {
  layout: Array<{
    i: string;
    x: number;
    y: number;
    w: number;
    h: number;
    minW?: number;
    maxW?: number;
    minH?: number;
    maxH?: number;
  }>;
  items: DashboardItem[];
  onLayoutChange: (layout: any[]) => void;
  renderWidget: (item: DashboardItem) => React.ReactNode;
}

const COLS = 12;

const DashboardGrid: React.FC<DashboardGridProps> = ({
  layout,
  items,
  onLayoutChange,
  renderWidget
}) => {
  const { i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  // Mirror x coordinates for RTL: x' = COLS - x - w
  const displayLayout = isRTL
    ? layout.map((l) => ({ ...l, x: COLS - l.x - l.w }))
    : layout;

  const handleLayoutChange = (newLayout: any[]) => {
    if (isRTL) {
      // Convert back from RTL to LTR for storage
      const ltrLayout = newLayout.map((l) => ({ ...l, x: COLS - l.x - l.w }));
      onLayoutChange(ltrLayout);
    } else {
      onLayoutChange(newLayout);
    }
  };

  return (
    // The grid is PHYSICALLY left-to-right in every locale: react-grid-layout places items with
    // `transform: translate(x)` measured from the left, and RTL is handled by mirroring `x` above.
    //
    // Pinned with the `dir` ATTRIBUTE. It was `direction: 'ltr'` in sx, which App.tsx's stylis RTL
    // plugin rewrites to `direction: rtl` under Arabic (measured 2026-10-21). In an RTL container an
    // absolutely positioned item with no left/right starts at the RIGHT edge, so `translate(404px)`
    // pushed every wide widget off-screen: the Arabic dashboard showed only the 4-column cards.
    <Box dir="ltr" sx={{ width: '100%', minHeight: '100vh' }}>
      <ReactGridLayout
        className="layout"
        layout={displayLayout}
        onLayoutChange={handleLayoutChange}
        cols={COLS}
        rowHeight={30}
        margin={[12, 12]}
        containerPadding={[0, 0]}
        draggableHandle=".widget-drag-handle > div"
        useCSSTransforms={true}
        allowOverlap={false}
        verticalCompact={true}
        compactType="vertical"
        preventCollision={false}
        isDraggable={true}
        isResizable={true}
        resizeHandles={['se']}
      >
        {items.map((item, index) => (
          <div key={item.id} {...(index === 0 ? { 'data-tour-widget': 'first' } : {})}>
            {/* Content gets the page's direction back: only the grid geometry is pinned LTR. */}
            <div dir={isRTL ? 'rtl' : 'ltr'} style={{ height: '100%' }}>
              {renderWidget(item)}
            </div>
          </div>
        ))}
      </ReactGridLayout>
    </Box>
  );
};

export default DashboardGrid;